import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, User, Square, SquarePen, GraduationCap, ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatMessageContent } from '@/lib/format-error';
import { InsufficientCreditsMessage } from '@/credits/components';
import { useAutoScroll } from '@/lib/hooks/useAutoScroll';
import { useSidebar } from '../../../context/SidebarContext';
import { useStreamingChat } from '../hooks/useStreamingChat';

interface FreeformViewProps {
  onBack: () => void;
}

export function FreeformView({ onBack }: FreeformViewProps) {
  const { isMobile } = useSidebar();
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { containerRef, anchorRef, scrollToBottomIfNeeded, scrollToBottom } = useAutoScroll();

  const { messages, isStreaming, sendMessage, stopStreaming, resetChat } = useStreamingChat();

  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, scrollToBottomIfNeeded]);

  useEffect(() => {
    if (!isStreaming && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isStreaming, isMobile]);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isStreaming) return;
    setInputValue('');
    setTimeout(() => scrollToBottom(), 50);
    await sendMessage(trimmedInput);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleReset = useCallback(() => {
    resetChat();
    setInputValue('');
    if (!isMobile) {
      textareaRef.current?.focus();
    }
  }, [resetChat, isMobile]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <GraduationCap className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Learn Anything</h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={isStreaming || messages.length === 0}
            className="gap-1.5"
          >
            <SquarePen className="h-4 w-4" />
            New Topic
          </Button>
        </div>
      </div>

      {/* Messages area */}
      <div ref={containerRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
              <GraduationCap className="h-16 w-16 mb-4 text-primary/80" />
              <h2 className="text-xl font-medium text-foreground mb-2">
                What would you like to learn?
              </h2>
              <p className="text-muted-foreground max-w-md mb-6">
                Ask about any topic and I'll help you learn it step by step. I can explain concepts,
                provide examples, and answer your questions.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {[
                  'Teach me about quantum computing',
                  'Explain how neural networks work',
                  'What is the Krebs cycle?',
                  'How does the stock market work?',
                ].map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setInputValue(suggestion);
                      if (!isMobile) {
                        textareaRef.current?.focus();
                      }
                    }}
                    className="text-sm"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-4',
                    message.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[80%] rounded-2xl px-4 py-3',
                      message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted',
                    )}
                  >
                    {message.role === 'assistant' && (
                      <div className="text-xs text-muted-foreground mb-1">AI Tutor</div>
                    )}
                    <div className="break-words">
                      {message.isStreaming && !message.content ? (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <div className="flex gap-0.5">
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: '0ms' }}
                            />
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: '150ms' }}
                            />
                            <span
                              className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"
                              style={{ animationDelay: '300ms' }}
                            />
                          </div>
                          <span className="text-sm">Preparing your lesson...</span>
                        </div>
                      ) : message.role === 'assistant' ? (
                        (() => {
                          const {
                            content: displayContent,
                            isError,
                            isInsufficientCredits,
                          } = formatMessageContent(message.content);
                          return (
                            <div
                              className={
                                isError
                                  ? 'prose prose-sm dark:prose-invert max-w-none text-amber-600 dark:text-amber-400'
                                  : 'prose prose-sm dark:prose-invert max-w-none'
                              }
                            >
                              {isError ? (
                                isInsufficientCredits ? (
                                  <InsufficientCreditsMessage />
                                ) : (
                                  displayContent
                                )
                              ) : (
                                <ReactMarkdown>{displayContent}</ReactMarkdown>
                              )}
                              {message.isStreaming && message.content && (
                                <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <>
                          {message.content}
                          {message.isStreaming && message.content && (
                            <span className="inline-block w-2 h-4 bg-current ml-0.5 animate-pulse" />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={anchorRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border bg-background">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <form onSubmit={handleSubmit} className="relative">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What would you like to learn about?"
              disabled={isStreaming}
              className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
              rows={1}
              style={{ height: 'auto' }}
            />
            {isStreaming ? (
              <Button
                type="button"
                size="icon"
                variant="destructive"
                onClick={stopStreaming}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
              >
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                disabled={!inputValue.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </form>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}
